// ⚡️ MESSAGE VALIDATION: Message validation logic for chat functionality
// Extracted from ChatPanel.js for better organization and security

import CryptoJS from 'crypto-js';
import { logSecurityEvent } from './chatUtils';

// Validate special message types with strict schema checking
export const validateMessageType = (msg) => {
  // Validate special message types with strict schema checking
  if (msg.type === 'order') {
    // Validate order message structure
    const requiredFields = ['product_name', 'product_price', 'order_id'];
    const missingFields = requiredFields.filter(field => !msg[field]);
    
    if (missingFields.length > 0) {
      logSecurityEvent('INVALID_ORDER_MESSAGE', { 
        missingFields, 
        messageId: msg.id 
      }, 'warning');
      return false;
    }
    
    // Validate price is a valid number
    if (isNaN(parseFloat(msg.product_price))) {
      logSecurityEvent('INVALID_ORDER_PRICE', { 
        price: msg.product_price, 
        messageId: msg.id 
      }, 'warning');
      return false;
    }
    
    return true;
  }
  
  if (msg.type === 'login_info') {
    // Block plain text login info - should be encrypted
    if (typeof msg.text === 'string' && msg.text.includes('password') && !msg.encrypted) {
      logSecurityEvent('PLAIN_TEXT_LOGIN_INFO', { 
        messageId: msg.id 
      }, 'error');
      return false;
    }
    return true;
  }
  
  if (msg.type === 'file') {
    // Validate file message structure
    if (!msg.fileUrl || !msg.fileName) {
      logSecurityEvent('INVALID_FILE_MESSAGE', { 
        messageId: msg.id 
      }, 'warning');
      return false;
    }
    return true;
  }
  
  // Default text message validation
  return true;
};

// Verify message integrity using hash
export const verifyMessageIntegrity = (msg) => {
  if (!msg.integrityHash) {
    logSecurityEvent('MESSAGE_INTEGRITY_MISSING', { 
      messageId: msg.id 
    }, 'warning');
    return { isValid: false, reason: 'missing_hash' };
  }
  
  // Helper function to safely get text content
  const getTextContent = (text) => {
    if (typeof text === 'string') return text;
    if (text && typeof text === 'object' && text.sanitized) return text.sanitized;
    return '';
  };
  
  // Recalculate expected hash
  const expectedHash = CryptoJS.SHA256(
    `${getTextContent(msg.text)}${msg.from || ''}${msg.timestamp || Date.now()}`
  ).toString();
  
  if (msg.integrityHash !== expectedHash) {
    logSecurityEvent('MESSAGE_INTEGRITY_FAILED', { 
      messageId: msg.id,
      expected: expectedHash,
      received: msg.integrityHash
    }, 'error');
    return { isValid: false, reason: 'hash_mismatch' };
  }
  
  return { isValid: true, reason: 'valid' };
};

// Validate socket message structure and content
export const validateSocketMessage = async (msg) => {
  if (!msg || typeof msg !== 'object') {
    logSecurityEvent('INVALID_SOCKET_MESSAGE_TYPE', { 
      type: typeof msg 
    }, 'error');
    return { isValid: false, error: 'Invalid message type' };
  }
  
  // Check required fields
  const requiredFields = ['id', 'text', 'from', 'timestamp'];
  const missingFields = requiredFields.filter(field => !msg[field]);
  
  if (missingFields.length > 0) {
    logSecurityEvent('MISSING_REQUIRED_FIELDS', { 
      missingFields, 
      messageId: msg.id 
    }, 'warning');
    return { isValid: false, error: `Missing required fields: ${missingFields.join(', ')}` };
  }
  
  // Validate message type
  if (!validateMessageType(msg)) {
    return { isValid: false, error: 'Invalid message type structure' };
  }
  
  // Verify integrity if hash is present
  if (msg.integrityHash) {
    const integrityCheck = verifyMessageIntegrity(msg);
    if (!integrityCheck.isValid) {
      return { isValid: false, error: `Integrity check failed: ${integrityCheck.reason}` };
    }
  }
  
  // Validate text content
  if (typeof msg.text !== 'string' && !(msg.text && typeof msg.text === 'object' && msg.text.sanitized)) {
    logSecurityEvent('INVALID_TEXT_CONTENT', { 
      messageId: msg.id,
      textType: typeof msg.text
    }, 'warning');
    return { isValid: false, error: 'Invalid text content' };
  }
  
  // Validate timestamp
  if (typeof msg.timestamp !== 'number' && !(msg.timestamp && typeof msg.timestamp.toDate === 'function')) {
    logSecurityEvent('INVALID_TIMESTAMP', { 
      messageId: msg.id,
      timestampType: typeof msg.timestamp
    }, 'warning');
    return { isValid: false, error: 'Invalid timestamp' };
  }
  
  // Validate sender
  if (typeof msg.from !== 'string' || msg.from.trim().length === 0) {
    logSecurityEvent('INVALID_SENDER', { 
      messageId: msg.id,
      sender: msg.from
    }, 'warning');
    return { isValid: false, error: 'Invalid sender' };
  }
  
  return { isValid: true, error: null };
};

// Sanitize message content
export const sanitizeMessageContent = (text) => {
  if (typeof text !== 'string') return text;
  
  // Basic XSS prevention - remove script tags and dangerous attributes
  const sanitized = text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/data:text\/html/gi, '');
  
  return sanitized;
};

// Validate message before sending
export const validateMessageBeforeSend = (message) => {
  const errors = [];
  
  // Check if message is empty
  if (!message || typeof message !== 'string') {
    errors.push('Message cannot be empty');
  } else {
    const trimmed = message.trim();
    if (trimmed.length === 0) {
      errors.push('Message cannot be empty');
    } else if (trimmed.length > 1000) {
      errors.push('Message too long (maximum 1000 characters)');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate file message
export const validateFileMessage = (file) => {
  const errors = [];
  
  if (!file) {
    errors.push('No file provided');
    return { isValid: false, errors };
  }
  
  // Check file type
  const allowedTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'video/mp4', 'video/webm', 'video/ogg'
  ];
  
  if (!allowedTypes.includes(file.type)) {
    errors.push(`File type ${file.type} is not allowed`);
  }
  
  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    errors.push(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds 5MB limit`);
  }
  
  // Check for empty files
  if (file.size === 0) {
    errors.push('File is empty');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate order message
export const validateOrderMessage = (orderData) => {
  const errors = [];
  
  if (!orderData) {
    errors.push('Order data is required');
    return { isValid: false, errors };
  }
  
  // Check required fields
  const requiredFields = ['product_name', 'product_price', 'order_id'];
  requiredFields.forEach(field => {
    if (!orderData[field]) {
      errors.push(`${field} is required`);
    }
  });
  
  // Validate price
  if (orderData.product_price && isNaN(parseFloat(orderData.product_price))) {
    errors.push('Product price must be a valid number');
  }
  
  // Validate order ID format
  if (orderData.order_id && typeof orderData.order_id !== 'string') {
    errors.push('Order ID must be a string');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
